import { AxiosError, AxiosRequestConfig } from "axios";
import { ApiResponse, ResponseStatus, ResponseCode } from "@types";
import instance from "./axios";
import { fetchAuthSession, signInWithRedirect } from "aws-amplify/auth";
import { clog } from "../logs/clog";
import { redirectAuth } from "./redirectAuth";

const postApi = async <T, P>(
  url: string,
  request: T,
  config?: AxiosRequestConfig,
): Promise<ApiResponse<P>> => {
  try {
    const reqUrl = `${url}`;

    const session = await fetchAuthSession({ forceRefresh: true });

    const authorizationHeader = session.tokens?.idToken;

    if (!authorizationHeader) {
      redirectAuth();
      return {
        status: ResponseStatus.FORBIDDEN,
        statusCode: ResponseCode.FORBIDDEN,
        message: "Unauthorized access. Please log in.",
      };
    }

    const configWithHeaders = config
      ? {
          headers: {
            Authorization: `Bearer ${authorizationHeader}`,
          },
          ...config,
        }
      : {
          headers: {
            Authorization: `Bearer ${authorizationHeader}`,
          },
        };

    const response = await instance.post<ApiResponse<P>>(
      reqUrl,
      request,
      configWithHeaders,
    );

    clog("API Response", response);
    if (
      response.data.status === ResponseStatus.SUCCESS ||
      response.data.statusCode === ResponseCode.SUCCESS
    ) {
      return response.data;
    }

    throw new AxiosError<ApiResponse<P>>(
      response.data.message,
      response.data.status || ResponseStatus.ERROR,
      undefined,
      undefined,
      response,
    );
  } catch (e) {
    const errorResponse = e as unknown as AxiosError<ApiResponse<unknown>>;
    clog("errorResponse", JSON.stringify(errorResponse));
    if (errorResponse.response?.status === ResponseCode.FORBIDDEN) {
      redirectAuth();
      return {
        status: ResponseStatus.FORBIDDEN,
        statusCode: ResponseCode.FORBIDDEN,
        message:
          errorResponse.response?.data?.message ||
          "Unauthorized access. Please log in.",
      };
    }
    return {
      status: errorResponse.response?.data?.status || ResponseStatus.ERROR,
      statusCode:
        errorResponse.response?.data?.statusCode || ResponseCode.SERVER_ERROR,
      message: errorResponse.response?.data?.message || "An error occurred",
    };
  }
};

export default postApi;
